import { useSyncExternalStore } from 'react';

// A deliberately tiny observable store. No external state library: the founding round keeps
// the state layer fully owned, typed and testable without a renderer.
export interface Store<T> {
  get(): T;
  set(updater: (prev: T) => T): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set: (updater) => {
      state = updater(state);
      for (const listener of [...listeners]) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

// Selectors must return stable references (slices held inside state) — derived arrays belong
// in useMemo at the component, not here, or React resubscribes forever.
export function useStore<T, S>(store: Store<T>, selector: (state: T) => S): S {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.get()),
    () => selector(store.get())
  );
}
